import { Sparkles, ShieldCheck, ArrowRight, FileCode, BadgeCheck, Store, Star, Users } from "lucide-react";
import { CodeBlock } from "@/components/CodeBlock";
import { Button } from "@/components/ui/button";
import { PRIMARY, SECONDARY, SAMPLE_SKILL, Reveal, Kicker } from "@/components/landing/shared";

export const Hero = ({ startTeam, stats }) => (
  <section className="rb-grain relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "radial-gradient(680px circle at 12% -4%, rgba(45,212,191,0.12), transparent 55%), radial-gradient(560px circle at 92% 0%, rgba(212,175,55,0.08), transparent 60%)" }} />
    <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-6 lg:pt-6">
        <Reveal>
          <Kicker><ShieldCheck className="h-3.5 w-3.5" /> A private, governed registry for Claude Code teams</Kicker>
          <h1 className="rb-display text-4xl font-semibold leading-[1.05] tracking-tight text-[color:var(--rb-text)] sm:text-5xl lg:text-6xl">
            Ship approved skills<br />to your whole team &mdash; <span className="text-[color:var(--rb-accent)]">automatically.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[color:var(--rb-text-dim)]">
            Author skills, agents, and commands. Every submission is safety-scanned and admin-approved before it ships. Engineers run <span className="rb-mono text-[color:var(--rb-accent)]">/runebox:login</span> once, and the whole approved catalog syncs to their Claude Code &mdash; and stays current. <span className="text-[color:var(--rb-text)]">Free for up to 5 seats.</span>
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button onClick={startTeam} data-testid="landing-hero-primary-cta" className={`h-12 rounded-xl px-6 text-base ${PRIMARY}`}>
              Start a team registry <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <Button onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })} data-testid="landing-hero-secondary-cta" className={`h-12 rounded-xl px-6 text-base ${SECONDARY}`}>
              See how it works
            </Button>
          </div>
          <p className="mt-4 flex items-center gap-2 text-sm text-[color:var(--rb-text-faint)]">
            <ShieldCheck className="h-4 w-4 text-[color:var(--rb-accent)]" /> Free for up to 5 seats &mdash; no credit card required.
          </p>
          <p className="mt-2 flex items-center gap-2 text-sm text-[color:var(--rb-text-faint)]">
            <Sparkles className="h-4 w-4 text-[color:var(--rb-accent)]" /> Works with Claude Code, Codex, Cursor, and Gemini CLI.
          </p>
        </Reveal>
      </div>

      <div className="lg:col-span-6">
        <Reveal delay={0.08}>
          <div data-testid="landing-hero-skill-preview" className="relative">
            <div className="absolute -left-3 -top-3 z-10 rotate-[-4deg]">
              <span className="rb-mono rounded-lg border border-[color:rgba(212,175,55,0.3)] bg-[color:var(--rb-gold-soft)] px-2.5 py-1 text-xs font-semibold text-[color:var(--rb-gold)] shadow-[0_8px_30px_rgba(212,175,55,0.12)]">Scanned &amp; approved</span>
            </div>
            <div className="absolute -right-2 top-10 z-10 rotate-[5deg]">
              <span className="rb-mono inline-flex items-center gap-1 rounded-lg border border-[color:rgba(124,247,230,0.28)] bg-[color:rgba(45,212,191,0.12)] px-2.5 py-1 text-xs font-semibold text-[color:var(--rb-accent-2)]"><BadgeCheck className="h-3.5 w-3.5" /> Synced to every engineer</span>
            </div>
            <div className="overflow-hidden rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] shadow-[0_24px_70px_rgba(0,0,0,0.5)]">
              <div className="flex items-center gap-2 border-b border-[color:var(--rb-border)] px-4 py-3">
                <FileCode className="h-4 w-4 text-[color:var(--rb-accent)]" />
                <span className="rb-mono text-xs text-[color:var(--rb-text-dim)]">invoice-parser/SKILL.md</span>
                <span className="ml-auto rb-mono text-[10px] text-[color:var(--rb-text-faint)]">+ scripts/parse.py</span>
              </div>
              <CodeBlock code={SAMPLE_SKILL} language="markdown" />
            </div>
          </div>
        </Reveal>
      </div>
    </div>

    <div data-testid="landing-social-proof-strip" className="relative border-y border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]/60">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-px px-4 sm:px-6 md:grid-cols-4">
        {[
          { v: `${stats.count}`, l: "Free in the gallery", i: Store },
          { v: stats.avg ? stats.avg.toFixed(1) : "5.0", l: "Avg verified rating", i: Star },
          { v: "Scan + approve", l: "Every submission, gated", i: ShieldCheck },
          { v: "Auto-sync", l: "Claude Code, Codex, Cursor & Gemini CLI", i: Users },
        ].map(({ v, l, i: Icon }) => (
          <div key={l} className="flex items-center gap-3 py-5">
            <Icon className="h-5 w-5 text-[color:var(--rb-accent)]" />
            <div>
              <div className="rb-display text-xl font-semibold text-[color:var(--rb-text)]">{v}</div>
              <div className="text-xs text-[color:var(--rb-text-faint)]">{l}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
